import type React from 'react';

import { InfoCircleIcon } from '@vlabs/icons';
import { TruncatedText } from '@vlabs/shared/components/truncated-text/TruncatedText';
import { formatDateTime, DATE_FORMATS } from '@vlabs/shared/utils/date';
import {
  Tag, Tooltip, useWindowSize, Control,
} from '@vlabs/uikit';
import type { WithClassNameProp, WithDataTestid } from '@vlabs/uikit/types';
import cn from 'classnames';
import { generatePath } from 'react-router-dom';

import {
  useApiData, useStreamQuery,
  useEventApi,
} from '@vlabs/monokit/entities/events/api';
import type { EventView } from '@vlabs/monokit/entities/events/api';
import type { EventListPermissions } from '@vlabs/monokit/entities/events/event-list-table/EventListPermissions';
import { buildTestid } from '@vlabs/monokit/shared/lib/utils';

import st from './EventInfoCell.module.sass';
import { useEventInfoCellTranslation } from './i18n';

interface EventInfoCellProps extends WithClassNameProp, WithDataTestid {
  event?: EventView;
  handlerRoute?: string;
  listRoute?: string;
  permissions?: Partial<EventListPermissions>;
}

const displayName = 'EventInfoCell';
const testid = buildTestid(displayName);

function InfoRow({
  label,
  value,
  tooltip = undefined,
  testId,
}: {
  label: string;
  value: React.ReactNode;
  tooltip?: string;
  testId: string;
}): React.JSX.Element {
  return (
    <div
      className={st.Row}
      {...testid(testId)}
    >
      <span className={st.Label}>
        {label}
        {!!tooltip && (
          <Tooltip
            tooltipContent={tooltip}
            tooltipProps={{ place: 'right' }}
          >
            <InfoCircleIcon className={st.TooltipIcon} />
          </Tooltip>
        )}
      </span>
      <span className={st.Value}>{value}</span>
    </div>
  );
}

function buildLocationString(event: EventView): string | null {
  const location = event?.location;
  const parts = [
    location?.area,
    location?.city,
    location?.district,
    location?.street,
    location?.house_number,
  ]
    .filter((v) => !!v)
    .join(', ');
  return parts.length > 0 ? parts : null;
}

function EventInfoCell({
  event = undefined,
  className = undefined,
  'data-testid': dataTestId = undefined,
  handlerRoute = undefined,
  listRoute = undefined,
  permissions = {},
}: EventInfoCellProps): React.JSX.Element | null {
  const { t } = useEventInfoCellTranslation();
  const api = useEventApi();
  const { width } = useWindowSize();
  const isSmall = width && width < 1920;
  const controlSize = isSmall ? 'medium' : 'large';

  const canReadHandler = permissions.canReadHandler ?? true;
  const canViewHandlers = permissions.canViewHandlersPage ?? true;
  const canReadStream = permissions.canReadStream ?? true;
  const canViewStreamWatch = permissions.canViewStreamWatch ?? true;
  const canViewStreamDetails = permissions.canViewStreamDetails ?? true;
  const canReadList = permissions.canReadList ?? true;
  const canViewListDetails = permissions.canViewListDetails ?? true;

  const { data: handlerOptionsData } = useApiData(async () => api.getHandlerOptions());
  const { data: listOptionsData } = useApiData(async () => api.getListOptions());
  const { data: stream } = useStreamQuery(event?.stream_id);

  if (!event) return null;

  const location = buildLocationString(event);
  const createTime = formatDateTime(event.create_time, DATE_FORMATS.withYear);
  const handlerOptions = handlerOptionsData ?? [];
  const listOptions = listOptionsData ?? [];

  const streamLabel = stream?.name ?? event.stream_id;

  let sourceValue = <span>{streamLabel}</span>;
  if (!!event.stream_id && (canViewStreamWatch || canViewStreamDetails)) {
    sourceValue = (
      <Control.Link
        kind="secondary"
        size={controlSize}
        to={
          canViewStreamWatch
            ? generatePath('/stream-watch/:streamId', { streamId: event.stream_id })
            : generatePath('/stream-details/:streamId', { streamId: event.stream_id })
        }
      >
        {streamLabel}
      </Control.Link>
    );
  }

  let handlerValue = (
    <span>
      {handlerOptions.find((h) => h.value === event.handler_id)?.label ?? event.handler_id}
    </span>
  );
  if (!!event.handler_id && canViewHandlers && handlerRoute) {
    handlerValue = (
      <Control.Link
        kind="secondary"
        size={controlSize}
        to={generatePath(handlerRoute, { handlerId: event.handler_id })}
      >
        {handlerOptions.find((h) => h.value === event.handler_id)?.label ?? event.handler_id}
      </Control.Link>
    );
  }

  return (
    <div
      className={cn(st.EventInfoCell, className)}
      {...testid(dataTestId)}
    >
      <div className={st.Header}>
        <Tag
          kind="neutral"
          size={controlSize}
          value={event.top_match ? t('match') : t('detection')}
          {...testid('Tag', dataTestId)}
        />
        {!!createTime && (
          <span className={st.Value}>
            {createTime}
          </span>
        )}
      </div>

      <div className={st.Rows}>
        {!!canReadStream && !!event.stream_id && (
          <InfoRow
            label={t('source')}
            testId="source"
            value={sourceValue}
          />
        )}
        {!!canReadHandler && !!event.handler_id && (
          <InfoRow
            label={t('handler')}
            testId="handler"
            value={handlerValue}
          />
        )}
        {!!canReadList && !!event.attach_result?.length && (
          <InfoRow
            label={t('lists')}
            testId="lists"
            tooltip={t('lists_tooltip')}
            value={(
              <div>
                {event.attach_result.map((listId, index) => {
                  if (canViewListDetails && listRoute) {
                    return (
                      <Control.Link
                        data-testid={`listLink${index + 1}`}
                        key={listId}
                        kind="secondary"
                        size={controlSize}
                        to={generatePath(listRoute, { listId })}
                      >
                        {listOptions.find((l) => l.value === listId)?.label ?? listId}
                      </Control.Link>
                    );
                  }
                  return (
                    <div key={listId}>
                      {listOptions.find((l) => l.value === listId)?.label ?? listId}
                    </div>
                  );
                })}
              </div>
            )}
          />
        )}
        {!!location && (
          <InfoRow
            label={t('location')}
            testId="location"
            value={(
              <TruncatedText
                hasTruncation
                tooltipContent={location}
              >
                {location}
              </TruncatedText>
            )}
          />
        )}
      </div>
    </div>
  );
}

EventInfoCell.displayName = displayName;

export { EventInfoCell };
